import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { toast } from "sonner";
import UserAutocomplete from "../components/UserAutocomplete";

export default function FixReviewersPage() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fixed, setFixed] = useState(0);
  const [saving, setSaving] = useState({});

  useEffect(() => { window.scrollTo(0, 0); load(); }, []);

  const load = async () => {
    try {
      const r = await api.get("/me/missing-reviewers?scope=all");
      setTasks(r.data?.tasks || []);
      setBriefs(r.data?.briefs || []);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  };

  const saveTaskReviewer = async (taskId, reviewerId) => {
    setSaving(s => ({ ...s, [taskId]: true }));
    try {
      await api.put(`/tasks/${taskId}`, { reviewer_id: reviewerId });
      toast.success("Reviewer assigned!");
      setTasks(prev => prev.filter(t => t.task_id !== taskId));
      setFixed(f => f + 1);
    } catch { toast.error("Failed to save"); }
    setSaving(s => ({ ...s, [taskId]: false }));
  };

  const saveBriefReviewer = async (briefId, reviewerId) => {
    setSaving(s => ({ ...s, [briefId]: true }));
    try {
      await api.put(`/bizdev/briefs/${briefId}`, { reviewer_1: reviewerId });
      toast.success("Reviewer assigned!");
      setBriefs(prev => prev.filter(b => b.brief_id !== briefId));
      setFixed(f => f + 1);
    } catch { toast.error("Failed to save"); }
    setSaving(s => ({ ...s, [briefId]: false }));
  };

  const total = tasks.length + briefs.length + fixed;
  const remaining = tasks.length + briefs.length;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-[var(--t3)] text-xl font-heading">LOADING...</div></div>;

  return (
    <div className="dm-page-container max-w-[700px] mx-auto" style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={() => navigate(-1)} style={{ padding: "6px 10px", background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 6, color: "var(--t2)", cursor: "pointer", fontSize: 16 }}>&larr;</button>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--t1)", fontFamily: "var(--font-mono)" }}>Fix Missing Reviewers</h2>
          <p style={{ fontSize: 12, color: "var(--t3)", fontFamily: "var(--font-mono)", marginTop: 2 }}>
            {remaining === 0 ? "All done!" : `${remaining} items need a reviewer`}
            {fixed > 0 && <span style={{ color: "var(--g)", marginLeft: 8 }}>{fixed} fixed</span>}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      {total > 0 && (
        <div style={{ height: 4, background: "var(--s2)", borderRadius: 2, marginBottom: 20, overflow: "hidden" }}>
          <div style={{ height: "100%", background: "var(--g)", borderRadius: 2, width: `${(fixed / total) * 100}%`, transition: "width .3s" }} />
        </div>
      )}

      {remaining === 0 && (
        <div style={{ textAlign: "center", padding: 40, background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>&#x2705;</div>
          <p style={{ fontSize: 15, fontWeight: 600, color: "var(--t1)" }}>All items have reviewers assigned!</p>
          <p style={{ fontSize: 12, color: "var(--t3)", marginTop: 4 }}>No tasks or briefs are missing a reviewer.</p>
          <button onClick={() => navigate("/me")} style={{ marginTop: 16, padding: "8px 20px", background: "var(--r)", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
            Back to Dashboard
          </button>
        </div>
      )}

      {/* Tasks missing reviewer */}
      {tasks.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".1em", color: "var(--t3)", textTransform: "uppercase", marginBottom: 10 }}>
            Tasks ({tasks.length})
          </h3>
          {tasks.map(t => (
            <div key={t.task_id} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
              background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 10,
              marginBottom: 8, animation: "fadeUp .2s ease-out",
            }}>
              <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => navigate(`/tasks/${t.task_id}`)}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: "underline", textDecorationColor: "var(--bdr)" }}>{t.title}</div>
                <div style={{ fontSize: 10, color: "var(--t3)", marginTop: 1 }}>Task &middot; {t.assignee_name || "Unassigned"}</div>
              </div>
              <div style={{ width: 180, flexShrink: 0 }}>
                <UserAutocomplete
                  value=""
                  onChange={(uid) => saveTaskReviewer(t.task_id, uid)}
                  placeholder="Assign reviewer"
                />
              </div>
              {saving[t.task_id] && <span style={{ fontSize: 10, color: "var(--t3)" }}>Saving...</span>}
            </div>
          ))}
        </div>
      )}

      {/* Briefs missing reviewer */}
      {briefs.length > 0 && (
        <div>
          <h3 style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".1em", color: "var(--t3)", textTransform: "uppercase", marginBottom: 10 }}>
            Briefs ({briefs.length})
          </h3>
          {briefs.map(b => (
            <div key={b.brief_id} style={{
              display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
              background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 10,
              marginBottom: 8, animation: "fadeUp .2s ease-out",
            }}>
              <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => navigate(`/bizdev/briefs/${b.brief_id}`)}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--t1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textDecoration: "underline", textDecorationColor: "var(--bdr)" }}>{b.title}</div>
                <div style={{ fontSize: 10, color: "var(--t3)", marginTop: 1 }}>Brief &middot; {b.company_name || ""}</div>
              </div>
              <div style={{ width: 180, flexShrink: 0 }}>
                <UserAutocomplete
                  value=""
                  onChange={(uid) => saveBriefReviewer(b.brief_id, uid)}
                  placeholder="Assign reviewer"
                />
              </div>
              {saving[b.brief_id] && <span style={{ fontSize: 10, color: "var(--t3)" }}>Saving...</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
